import ReactDatePicker, { CalendarContainer } from "react-datepicker";
import React, { FC, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { MDBBtn } from "mdb-react-ui-kit";
import styles2 from "@/styles/detail/DetailAddToCart.module.scss";
import { useStore } from "@/store/index";
import TagManager from "react-gtm-module";
import { getCookie } from "@/utils/common/cookies";
import DatePickerRangeButton from "./DatePickerRangeButton";

interface HighlightDates {
    [className: string]: Date[];
}

const MyContainer = ({
    children,
    isClosed,
}: {
    children: React.ReactNode;
    isClosed: boolean;
}) => {
    return (
        <div
            style={{
                padding: "20px",
                background: "#F1F2F4",
                color: "#fff",
                borderRadius: "20px",
                display: isClosed ? "none" : "",
            }}
        >
            <CalendarContainer>
                <div style={{ position: "relative" }}>{children}</div>
            </CalendarContainer>
        </div>
    );
};

const RangeDatePicker: FC<{
    selectedDate: Date | null;
    startDateView: Date | null;
    lastDate: Date;
    setSelectedDate: (date: Date) => void;
    setStartDateView: (date: Date | null) => void;
    excludeDates: string[];
    discountedDates: string[];
    rangeLength: number;
    setClick?: React.Dispatch<React.SetStateAction<boolean>>;
    click?: boolean;
    label?: string;
    promoDaysPresent?: boolean;
    onDeleteRange?: () => void;
}> = ({
    selectedDate,
    lastDate,
    setSelectedDate,
    setStartDateView,
    excludeDates,
    discountedDates,
    rangeLength,
    click,
    setClick,
    startDateView,
    label,
    promoDaysPresent = true,
    onDeleteRange,
}) => {
    const eDates = useMemo(
        () =>
            excludeDates
                .map(date => new Date(date))
                .sort((a, b) => moment(a).diff(moment(b))),
        [excludeDates]
    );

    const startDate = useMemo(() => {
        if (
            selectedDate &&
            eDates.some(
                element =>
                    element.toDateString() === selectedDate.toDateString()
            )
        ) {
            const excludeDate = eDates.find(
                (currentElement, index, arr) =>
                    !moment(currentElement)
                        .add(1, "day")
                        .isSame(arr.at(index + 1)) &&
                    moment(currentElement).add(1, "day").isAfter(selectedDate)
            );
            const result = moment(excludeDate).add(1, "day").toDate();
            setSelectedDate(result);
            return result;
        }

        return selectedDate;
    }, [eDates, selectedDate, setSelectedDate]);

    const endDate = useMemo(() => {
        return moment(startDate)
            .add(rangeLength - 1, "day")
            .toDate();
    }, [startDate, rangeLength]);

    const highlightWithRanges: HighlightDates[] = useMemo(
        () => [
            {
                "react-datepicker__day--highlighted-custom-3":
                    discountedDates.map(value => moment(value).toDate()),
            },
        ],
        [discountedDates]
    );

    const [openDataFirst, setOpenDataFirst] = useState<boolean>(false);
    const [isClosed, setIsClosed] = useState<boolean>(false);
    // const [isConfirm, setIsConfirm] = useState<boolean>(
    //     router.query.isConfirm
    //         ? JSON.parse(router.query.isConfirm as string)
    //         : false
    // );
    const [isConfirm, setIsConfirm] = useState<boolean>(false);

    const [eventSent, setEventSent] = useState<boolean>(false);

    const dispatchCheckout = useStore(state => state.dispatchCheckout);

    useEffect(() => {
        if (openDataFirst && isConfirm && !eventSent) {
            TagManager.dataLayer({
                dataLayer: {
                    event: "initiate_booking",
                    fbClickID: getCookie("_fbc"),
                },
            });
            setEventSent(true);
        }
    }, [isConfirm, openDataFirst, eventSent]);

    return (
        <ReactDatePicker
            className="w-100"
            selected={null}
            onChange={date => {
                setOpenDataFirst(true);
                if (date) {
                    setSelectedDate(date);
                    setStartDateView(date);
                    // const url = new URL(window.location.href);
                    // url.searchParams.set(
                    //     "selectedDate",
                    //     date.toLocaleDateString()
                    // );
                    // window.history.replaceState(null, "", url);
                }
            }}
            excludeDates={eDates}
            highlightDates={highlightWithRanges}
            minDate={moment().toDate()}
            maxDate={lastDate}
            onCalendarClose={() => {
                if (startDateView && endDate) {
                    setIsConfirm(true);
                    setIsClosed(true);
                    // const url = new URL(window.location.href);
                    // url.searchParams.set(
                    //     "isConfirm",
                    //     true as unknown as string
                    // );
                    // window.history.replaceState(null, "", url);
                    dispatchCheckout({
                        type: "SET_CLICKCONFIRMDATE",
                        payload: true,
                    });
                }
            }}
            startDate={startDateView}
            popperClassName={`${isClosed ? styles2.removePopper : ""}`}
            endDate={endDate}
            customInput={
                <DatePickerRangeButton
                    startDate={selectedDate}
                    rangeLength={rangeLength}
                    click={click}
                    setClick={setClick}
                    setIsClosed={setIsClosed}
                    isConfirm={isConfirm}
                    setIsConfirm={setIsConfirm}
                    setStartDateView={setStartDateView}
                    label={label}
                    onDeleteRange={onDeleteRange}
                />
            }
            dateFormat="MMMM d, yyyy"
            disabledKeyboardNavigation
            shouldCloseOnSelect={false}
            calendarContainer={item =>
                MyContainer({ children: item.children, isClosed })
            }
        >
            <div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "start",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                width: "20px",
                                height: "20px",
                                borderRadius: "100%",
                                background: startDateView ? "#55CE97" : "white",
                                marginRight: "10px",
                            }}
                        />
                        <span className="legend-react-datepicker-text">
                            {startDateView
                                ? "Selected dates"
                                : "Available dates"}
                        </span>
                    </div>
                    {promoDaysPresent && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "start",
                                alignItems: "center",
                            }}
                        >
                            <div
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "100%",
                                    background: "#92A5EF",
                                    marginRight: "10px",
                                }}
                            />
                            <span className="legend-react-datepicker-text">
                                Promo days
                            </span>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: "15px" }}>
                    <MDBBtn
                        className={styles2.addToCartButton}
                        onClick={() => {
                            setIsConfirm(true);
                            setIsClosed(true);
                            // const url = new URL(window.location.href);
                            // url.searchParams.set(
                            //     "isConfirm",
                            //     true as unknown as string
                            // );
                            // window.history.replaceState(null, "", url);
                            dispatchCheckout({
                                type: "SET_CLICKCONFIRMDATE",
                                payload: true,
                            });
                        }}
                    >
                        <span>Confirm your vacation dates</span>
                    </MDBBtn>
                </div>
            </div>
        </ReactDatePicker>
    );
};

export default RangeDatePicker;
